/**
 * Dialog (centred), Drawer (side panel; bottom sheet on phones) and the confirm
 * dialogs. Escape closes, focus moves in on open and back on close, Tab is trapped,
 * the page behind does not scroll.
 */
import { X } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Button, cx } from './core';
import { TextArea } from './form';

function useModal(open: boolean, onClose: () => void) {
  const panel = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const before = document.activeElement as HTMLElement | null;
    const t = setTimeout(() => {
      const first = panel.current?.querySelector<HTMLElement>('[autofocus], input, select, textarea, button:not([data-close])');
      (first ?? panel.current)?.focus();
    }, 0);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeRef.current();
      }
      if (e.key === 'Tab' && panel.current) {
        const f = [...panel.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])')];
        if (!f.length) return;
        const [a, z] = [f[0], f[f.length - 1]];
        if (e.shiftKey && document.activeElement === a) {
          e.preventDefault();
          z.focus();
        } else if (!e.shiftKey && document.activeElement === z) {
          e.preventDefault();
          a.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      before?.focus?.();
    };
  }, [open]);
  return panel;
}

interface OverlayProps {
  open: boolean;
  title: ReactNode;
  description?: ReactNode;
  onClose: () => void;
  children?: ReactNode;
  footer?: ReactNode;
  /** Dialog width: sm 420, md 560, lg 760. */
  size?: 'sm' | 'md' | 'lg';
}

function Head({ id, title, description, onClose }: { id: string; title: ReactNode; description?: ReactNode; onClose: () => void }) {
  return (
    <div className="flex items-start gap-3 border-b border-line px-5 py-3.5">
      <div className="min-w-0 flex-1">
        <h2 id={id} className="text-lg font-semibold leading-snug">
          {title}
        </h2>
        {description && <p className="mt-0.5 text-sm text-muted">{description}</p>}
      </div>
      <button type="button" data-close onClick={onClose} aria-label="Close" className="-mr-2 grid h-11 w-11 shrink-0 place-items-center rounded-tag text-muted hover:bg-sunken hover:text-ink">
        <X aria-hidden className="h-5 w-5" />
      </button>
    </div>
  );
}

const WIDTH = { sm: 'sm:max-w-[420px]', md: 'sm:max-w-[560px]', lg: 'sm:max-w-[760px]' };

export function Dialog({ open, title, description, onClose, children, footer, size = 'md' }: OverlayProps) {
  const panel = useModal(open, onClose);
  const id = useId();
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-6">
      <div aria-hidden className="absolute inset-0 bg-[rgb(var(--shadow)/0.5)]" onClick={onClose} />
      <div ref={panel} role="dialog" aria-modal="true" aria-labelledby={id} tabIndex={-1} className={cx('relative flex max-h-[92dvh] w-full flex-col rounded-t-panel bg-card shadow-lift outline-none sm:rounded-panel', WIDTH[size])}>
        <Head id={id} title={title} description={description} onClose={onClose} />
        <div className="overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="safe-bottom flex flex-wrap justify-end gap-2 border-t border-line px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
}

/** Side panel for detail views and filters; a bottom sheet below 640 px. */
export function Drawer({ open, title, description, onClose, children, footer, side = 'right' }: OverlayProps & { side?: 'right' | 'left' }) {
  const panel = useModal(open, onClose);
  const id = useId();
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-stretch" style={{ justifyContent: side === 'right' ? 'flex-end' : 'flex-start' }}>
      <div aria-hidden className="absolute inset-0 bg-[rgb(var(--shadow)/0.45)]" onClick={onClose} />
      <div ref={panel} role="dialog" aria-modal="true" aria-labelledby={id} tabIndex={-1} className="relative flex max-h-[90dvh] w-full flex-col rounded-t-panel bg-card shadow-lift outline-none sm:max-h-none sm:w-[440px] sm:rounded-none">
        <Head id={id} title={title} description={description} onClose={onClose} />
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="safe-bottom flex flex-wrap justify-end gap-2 border-t border-line px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
}

export function Confirm({ open, title, body, confirmLabel, danger, loading, onConfirm, onClose }: { open: boolean; title: string; body?: ReactNode; confirmLabel: string; danger?: boolean; loading?: boolean; onConfirm: () => void; onClose: () => void }) {
  return (
    <Dialog
      open={open}
      title={title}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {body && <div className="text-sm text-muted">{body}</div>}
    </Dialog>
  );
}

/**
 * Every status change, void, write-off or reconciliation needs a reason (FR-INV-03,
 * FR-AUD-05). Reasons can be picked from presets and/or typed; minimum length enforced.
 */
export function ConfirmWithReason({
  open,
  title,
  body,
  confirmLabel,
  danger,
  loading,
  presets,
  minLength = 5,
  onConfirm,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  body?: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  loading?: boolean;
  presets?: string[];
  minLength?: number;
  onConfirm: (reason: string) => void;
  onClose: () => void;
  children?: ReactNode;
}) {
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) setReason('');
  }, [open]);
  const ok = reason.trim().length >= minLength;
  return (
    <Dialog
      open={open}
      title={title}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} loading={loading} disabled={!ok} onClick={() => onConfirm(reason.trim())}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {body && <div className="mb-3 text-sm text-muted">{body}</div>}
      {children}
      {presets && presets.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2" aria-label="Common reasons">
          {presets.map((p) => (
            <button key={p} type="button" onClick={() => setReason(p)} className={cx('min-h-9 rounded-full border px-3 text-sm', reason === p ? 'border-brand bg-brand-soft text-brand' : 'border-line hover:bg-sunken')}>
              {p}
            </button>
          ))}
        </div>
      )}
      <TextArea label="Reason" hint={`Recorded in the activity log. At least ${minLength} characters.`} value={reason} rows={3} onChange={(e) => setReason(e.target.value)} />
    </Dialog>
  );
}
